'use client';

import Image from 'next/image';
import { useState } from 'react';
import { useTranslations } from 'next-intl';
import { useRelativeTime } from '../../hooks/useRelativeTime';
import { daemonTriggerStatusBadge, daemonTriggerStatusLabel } from '../../lib/badgeColors';
import { cn } from '../../lib/cn';
import { getRunnerTypeIcon, getRunnerTypeLabel } from '../daemonTriggerDetailUtils';
import { DaemonTriggerQueuePositionBadge } from '../DaemonTriggerQueuePositionBadge';
import { QuestionsForUserBadge } from '../QuestionsForUserBadge';
import type { DaemonTrigger, MeActiveRun } from '../../lib/api';

type RecentRunsListProps = {
  runs: MeActiveRun[];
  isLoading: boolean;
  error?: unknown;
  onRetry?: () => void;
  onSelect: (run: MeActiveRun) => void;
  busyRunId?: string | null;
  limit?: number;
  emptyText?: string;
  errorText?: string;
  retryText?: string;
};

const WidgetRowsSkeleton = () => (
  <ul className="space-y-2" aria-hidden data-testid="recent-runs-skeleton">
    {Array.from({ length: 3 }).map((_, index) => (
      <li key={index} className="animate-pulse rounded-lg border border-base-200 p-2">
        <div className="flex items-start gap-2">
          <div className="min-w-0 flex-1 space-y-2">
            <div className="h-3 w-3/4 rounded bg-base-200" />
            <div className="h-2.5 w-1/2 rounded bg-base-200" />
          </div>
          <div className="h-4 w-12 rounded-full bg-base-200" />
        </div>
      </li>
    ))}
  </ul>
);

type ChainGroup = {
  chainKey: string;
  attempts: MeActiveRun[];
  latest: MeActiveRun;
  kind: 'RETRY' | 'PLAN_TASK';
};

type RunnerTypeIconProps = {
  label: string;
  runnerType: string;
  size: number;
};

const RunnerTypeIcon = ({ label, runnerType, size }: RunnerTypeIconProps) => {
  const icon = getRunnerTypeIcon(runnerType);

  return (
    <Image
      src={icon.src}
      alt={label}
      title={label}
      width={size}
      height={size}
      className={cn('shrink-0', icon.mono && 'dark:invert')}
    />
  );
};

const buildChainGroups = (runs: MeActiveRun[]): ChainGroup[] => {
  const attemptsByChainKey = new Map<string, MeActiveRun[]>();
  const chainOrder: string[] = [];

  for (const run of runs) {
    const chainKey = run.chainId ?? run.id;
    if (!attemptsByChainKey.has(chainKey)) {
      attemptsByChainKey.set(chainKey, []);
      chainOrder.push(chainKey);
    }
    (attemptsByChainKey.get(chainKey) as MeActiveRun[]).push(run);
  }

  return chainOrder.map((chainKey) => {
    const attempts = attemptsByChainKey.get(chainKey) as MeActiveRun[];
    const latest = attempts[0];
    return { chainKey, attempts, latest, kind: latest.chainKind === 'PLAN_TASK' ? 'PLAN_TASK' : 'RETRY' };
  });
};

/**
 * 홈의 최근 러너 위젯과 캡처 이어쓰기 picker가 공유하는 chain 그룹화된 러너 요청 리스트.
 * 자체 fetch는 하지 않으며, 상위가 runs/isLoading/onSelect 등을 주입한다. picker는 busyRunId로
 * 항목별 비동기 disabled 표현을 추가한다.
 */
export const RecentRunsList = ({
  runs,
  isLoading,
  error,
  onRetry,
  onSelect,
  busyRunId = null,
  limit,
  emptyText,
  errorText,
  retryText,
}: RecentRunsListProps) => {
  const t = useTranslations('home');
  const tLayout = useTranslations('components.appLayout');
  const relativeTime = useRelativeTime();
  const formatRelative = (iso: string | null): string => (iso ? relativeTime(iso) : '');
  const [expandedChainKeys, setExpandedChainKeys] = useState<Set<string>>(new Set());

  const toggleChain = (chainKey: string) => {
    setExpandedChainKeys((prev) => {
      const next = new Set(prev);
      if (next.has(chainKey)) {
        next.delete(chainKey);
      } else {
        next.add(chainKey);
      }
      return next;
    });
  };

  if (isLoading) return <WidgetRowsSkeleton />;

  if (error) {
    return (
      <div className="rounded-lg border border-error/20 bg-error/5 p-3">
        <p className="text-xs font-medium text-error">{errorText ?? t('recentRunsLoadFailed')}</p>
        {onRetry ? (
          <button
            type="button"
            onClick={onRetry}
            className="btn btn-ghost btn-xs mt-2 px-2 text-error hover:bg-error/10"
          >
            {retryText ?? t('recentRunsRetry')}
          </button>
        ) : null}
      </div>
    );
  }

  const chainGroups = limit ? buildChainGroups(runs).slice(0, limit) : buildChainGroups(runs);

  if (chainGroups.length === 0) {
    return <p className="text-xs text-[var(--color-fg-muted)]">{emptyText ?? t('recentRunsEmpty')}</p>;
  }

  return (
    <ul className="space-y-2">
      {chainGroups.map((group) => {
        const { latest } = group;
        const statusBadge =
          daemonTriggerStatusBadge[latest.status as DaemonTrigger['status']] ??
          'bg-[var(--chip-neutral-bg)] text-[var(--chip-neutral-fg)]';
        const statusLabel = daemonTriggerStatusLabel[latest.status as DaemonTrigger['status']] ?? latest.status;
        const elapsed = formatRelative(latest.updatedAt ?? latest.startedAt ?? latest.createdAt);
        const runnerTypeLabel = getRunnerTypeLabel(latest.runnerType, tLayout);
        const hiddenRuns = group.attempts.slice(1);
        const hasHiddenRuns = hiddenRuns.length > 0;
        const isExpanded = expandedChainKeys.has(group.chainKey);
        const isLatestBusy = busyRunId === latest.id;

        return (
          <li key={group.chainKey} className="space-y-1">
            <div
              role="button"
              tabIndex={isLatestBusy ? -1 : 0}
              aria-disabled={isLatestBusy || undefined}
              onClick={() => {
                if (isLatestBusy) return;
                onSelect(latest);
              }}
              onKeyDown={(event) => {
                if (isLatestBusy) return;
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault();
                  onSelect(latest);
                }
              }}
              className={`flex w-full min-w-0 cursor-pointer items-start gap-2 rounded-lg border border-base-200 p-2 text-left transition hover:bg-base-200 ${
                isLatestBusy ? 'cursor-wait opacity-60' : ''
              }`}
            >
              <div className="min-w-0 flex-1">
                <div className="flex min-w-0 items-center gap-1.5">
                  <p className="min-w-0 truncate text-sm font-medium text-base-content">
                    {group.kind === 'PLAN_TASK'
                      ? (latest.chainPlanTitle ??
                        latest.promptPreview ??
                        latest.agentConfigName ??
                        latest.id.slice(0, 8))
                      : latest.promptPreview || latest.agentConfigName || latest.id.slice(0, 8)}
                  </p>
                  {group.kind === 'PLAN_TASK' &&
                  latest.chainTaskDoneCount !== null &&
                  latest.chainTaskTotalCount !== null ? (
                    <span className="shrink-0 rounded-full bg-[var(--chip-neutral-bg)] px-1.5 py-0.5 text-[10px] font-medium text-[var(--chip-neutral-fg)]">
                      {t('recentRunsPlanTaskProgress', {
                        done: latest.chainTaskDoneCount,
                        total: latest.chainTaskTotalCount,
                      })}
                    </span>
                  ) : null}
                </div>
                {group.kind === 'PLAN_TASK' && latest.chainCurrentTaskTitle ? (
                  <p className="mt-0.5 truncate text-[11px] text-[var(--color-fg-muted)]">
                    {latest.chainCurrentTaskTitle}
                  </p>
                ) : null}
                <div className="mt-1 flex min-w-0 items-center gap-1.5 text-xs text-[var(--color-fg-subtle)]">
                  <span className="min-w-0 max-w-[14rem] truncate rounded-md bg-base-200 px-1.5 py-0.5 font-medium text-[var(--color-fg-subtle)]">
                    {latest.projectName ?? latest.projectId.slice(0, 8)}
                  </span>
                  {latest.daemonName ? (
                    <>
                      <span className="shrink-0">·</span>
                      <span className="min-w-0 truncate">{latest.daemonName}</span>
                    </>
                  ) : null}
                  {/* 드롭으로 만든 요청은 러너·엔진을 사용자가 고르지 않는다. 어떤 엔진이 도는지
                      여기서 못 보면 요청을 지우고 모달로 다시 넣는 수밖에 없다. */}
                  <span className="shrink-0">·</span>
                  <RunnerTypeIcon label={runnerTypeLabel} runnerType={latest.runnerType} size={12} />
                  {elapsed ? (
                    <>
                      <span className="shrink-0">·</span>
                      <span className="shrink-0">{elapsed}</span>
                    </>
                  ) : null}
                </div>
              </div>
              <div className="flex shrink-0 flex-col items-end gap-1">
                <span className="inline-flex items-center gap-1">
                  <QuestionsForUserBadge hasQuestions={latest.hasQuestionsForUser} size="xs" />
                  <DaemonTriggerQueuePositionBadge
                    status={latest.status}
                    queuePosition={latest.queuePosition}
                    queueRunnerState={latest.queueRunnerState}
                    size="xs"
                  />
                  <span className={`rounded-full px-2 py-0.5 text-[10px] font-medium ${statusBadge}`}>
                    {statusLabel}
                  </span>
                </span>
                {hasHiddenRuns ? (
                  <button
                    type="button"
                    onClick={(event) => {
                      event.stopPropagation();
                      toggleChain(group.chainKey);
                    }}
                    className="inline-flex items-center rounded-full bg-[var(--chip-neutral-bg)] px-2 py-0.5 text-[10px] font-medium text-[var(--chip-neutral-fg)] transition hover:bg-base-300"
                  >
                    {group.kind === 'PLAN_TASK'
                      ? isExpanded
                        ? t('recentRunsHidePlanTasks')
                        : t('recentRunsShowPlanTasks', { count: hiddenRuns.length })
                      : isExpanded
                        ? t('recentRunsHidePriorAttempts')
                        : t('recentRunsShowPriorAttempts', { count: hiddenRuns.length })}
                  </button>
                ) : null}
              </div>
            </div>
            {hasHiddenRuns && isExpanded ? (
              <ul className="ml-3 space-y-1 border-l border-base-200 pl-2">
                {hiddenRuns.map((attempt) => {
                  const attemptStatusBadge =
                    daemonTriggerStatusBadge[attempt.status as DaemonTrigger['status']] ??
                    'bg-[var(--chip-neutral-bg)] text-[var(--chip-neutral-fg)]';
                  const attemptStatusLabel =
                    daemonTriggerStatusLabel[attempt.status as DaemonTrigger['status']] ?? attempt.status;
                  const attemptElapsed = formatRelative(attempt.updatedAt ?? attempt.startedAt ?? attempt.createdAt);
                  const attemptRunnerTypeLabel = getRunnerTypeLabel(attempt.runnerType, tLayout);
                  const isAttemptBusy = busyRunId === attempt.id;
                  return (
                    <li key={attempt.id}>
                      <button
                        type="button"
                        onClick={() => onSelect(attempt)}
                        disabled={isAttemptBusy}
                        className="flex w-full min-w-0 items-start gap-2 rounded-lg border border-base-200 bg-base-200 p-2 text-left transition hover:bg-base-300 disabled:cursor-wait disabled:opacity-60"
                      >
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-1">
                            <span className="shrink-0 rounded-full bg-[var(--chip-neutral-bg)] px-1.5 py-0.5 text-[9px] font-medium text-[var(--chip-neutral-fg)]">
                              {group.kind === 'PLAN_TASK' ? t('recentRunsPlanTask') : t('recentRunsPreviousAttempt')}
                            </span>
                            <p className="truncate text-[11px] text-base-content/80">
                              {attempt.promptPreview || attempt.agentConfigName || attempt.id.slice(0, 8)}
                            </p>
                          </div>
                          <div className="mt-0.5 flex items-center gap-1 text-[10px] text-[var(--color-fg-subtle)]">
                            <RunnerTypeIcon label={attemptRunnerTypeLabel} runnerType={attempt.runnerType} size={10} />
                            {attemptElapsed ? (
                              <>
                                <span aria-hidden="true">·</span>
                                <span>{attemptElapsed}</span>
                              </>
                            ) : null}
                          </div>
                        </div>
                        <span className="inline-flex shrink-0 items-center gap-1">
                          <QuestionsForUserBadge hasQuestions={attempt.hasQuestionsForUser} size="xs" />
                          <DaemonTriggerQueuePositionBadge
                            status={attempt.status}
                            queuePosition={attempt.queuePosition}
                            queueRunnerState={attempt.queueRunnerState}
                            size="xs"
                          />
                          <span className={`rounded-full px-2 py-0.5 text-[10px] font-medium ${attemptStatusBadge}`}>
                            {attemptStatusLabel}
                          </span>
                        </span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            ) : null}
          </li>
        );
      })}
    </ul>
  );
};
